import React, { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { FieldTypes } from '../../utils/constants'
import useApartmentsApi from '../../hooks/apartments/useApartmentsApi'
import Form from '../common/Form'
import useUserEventsApi from '../../hooks/userEvents/useUserEventsApi'
import useUserInvitationsApi from '../../hooks/userInvitations/useUserInvitationsApi'
import ListHeader from '../common/ListHeader'
import useStyles from '../common/FormFields/FormFieldsStyle'
import { useLocation } from 'react-router-dom'
import useBuildingsApi from '../../hooks/buildings/useBuildingsApi'
import { formatInvitationOrigin } from '../../utils/invitationOrigin'

export default function GuestForm({
  building,
  handleCancel,
  createUserInvitation,
  updateUserInvitation,
  type,
  invitation,
}) {
  const classes = useStyles()
  const inputRef = useRef()
  const location = useLocation()
  const { building: buildingFromRoute } = location.state || {}

  if (!building) {
    building = invitation?.building || buildingFromRoute
  }

  const [values, setValues] = useState(
    invitation
      ? {
          firstName: invitation.firstName,
          lastName: invitation.lastName,
          email: invitation.email,
          phone: invitation.phone,
          ci: invitation.ci,
          observations: invitation.observations,
          type: invitation.serviceTypeId,
          company: invitation.company,
          apartments: invitation.apartments,
        }
      : {}
  )

  const [invitationType, setInvitationType] = useState(
    invitation && (invitation.apartments ?? []).length === 0 ? 2 : 1
  )
  const [schedules, setSchedules] = useState(
    invitation?.schedules.map((schedule) => ({
      startTime: schedule.startTime,
      endTime: schedule.endTime,
      days: schedule.days,
      type: schedule.type,
    })) ?? []
  )

  const [selfie, setSelfie] = useState(null)
  const [selectedBuilding, setSelectedBuilding] = useState(null)

  const [{ getApartmentsFromBuildingState }, { getApartmentsFromBuilding }] =
    useApartmentsApi()
  const [{ eventsState }, methods] = useUserEventsApi()
  const [{ userInvitationState }, { getSettings }] = useUserInvitationsApi()
  const [{ buildingsState }, { getBuildings }] = useBuildingsApi()

  useEffect(() => {
    const fetchData = async (params) => {
      methods.getEvents(params)
      if (building?.id) {
        getApartmentsFromBuilding(building?.id)
      } else {
        getBuildings()
      }
      // Only the service types are used here; getInvitations() also loaded
      // every invitation the user can see.
      getSettings()
    }
    fetchData()
  }, [])

  const updateScheduleData = (index, newElement) => {
    if (index >= 0 && index < schedules.length) {
      const updatedSchedules = [...schedules]
      updatedSchedules[index] = newElement
      setSchedules(updatedSchedules)
    }
  }

  const handleSelectFile = (event) => {
    event.preventDefault()
    var files = event.target.files,
      f = files[0]
    var reader = new FileReader()
    reader.onload = (e) => {
      var data = e.target.result
      // setValues({ ...values, image: data })
      setSelfie(data)
    }
    reader.readAsDataURL(f)
  }

  const removeSchedule = (index) => {
    if (index >= 0 && index < schedules.length) {
      const updatedSchedules = [...schedules]
      updatedSchedules.splice(index, 1)
      setSchedules(updatedSchedules)
    }
  }

  return (
    <div className={classes.paper}>
      <ListHeader
        bigTitle={
          type === 'services'
            ? invitation
              ? 'Editar servicio autorizado'
              : 'Nuevo servicio autorizado'
            : 'Nuevo autorizado'
        }
        canCreate={false}
      />
      {invitation && (
        <p style={{ margin: '0 0 16px' }}>
          Creado por:{' '}
          {formatInvitationOrigin(invitation) ||
            'sin datos (creado antes de que se registrara)'}
        </p>
      )}
      <Form
        values={values}
        setValues={setValues}
        fields={[
          {
            type: FieldTypes.TextField,
            label: 'Nombre',
            hint: 'Ingrese un nombre',
            key: 'firstName',
            errorMessage: 'El nombre es requerido',
            onlyLetters: true,
            capitalized: true,
          },
          {
            type: FieldTypes.TextField,
            label: 'Apellido',
            hint: 'Ingrese un apellido',
            key: 'lastName',
            errorMessage: 'El apellido es requerido',
            onlyLetters: true,
            capitalized: true,
          },
          {
            type: FieldTypes.TextField,
            label: 'Email',
            hint: 'Ingrese un email',
            key: 'email',
          },
          {
            type: FieldTypes.TextField,
            label: 'Teléfono',
            hint: 'Ingrese un teléfono',
            key: 'phone',
            numberic: true,
          },
          {
            type: FieldTypes.TextField,
            label: 'Documento de identidad',
            hint: 'Ingresar documento',
            key: 'ci',
            errorMessage: 'El documento de identidad es requerido',
          },
          {
            type: FieldTypes.TextField,
            label: 'Observaciones',
            hint: 'Ingrese observaciones',
            key: 'observations',
          },
          ...(type === 'services'
            ? [
                {
                  type: FieldTypes.Select,
                  id: 'service-type',
                  label: 'Tipo de servicio',
                  hint: 'Seleccione un tipo de servicio',
                  key: 'type',
                  errorMessage: 'El tipo de servicio es requerido',
                  withMargin: true,
                  renderValue: (selected) => {
                    if (!selected) {
                      return (
                        <span
                          className={clsx(
                            classes.inputFont,
                            classes.selectPlaceholder
                          )}
                        >
                          Seleccione un tipo de servicio
                        </span>
                      )
                    }
                    const selectedOption =
                      userInvitationState?.serviceTypes?.find(
                        (option) =>
                          option.id === selected ||
                          option.name.replaceAll('_', ' ') === selected ||
                          option.name === selected
                      )
                    return selectedOption ? selectedOption.displayName : null
                  },
                  options: userInvitationState?.serviceTypes
                    ? userInvitationState?.serviceTypes?.map((type) => ({
                        value: type.id,
                        label: type.displayName,
                      }))
                    : [],
                },
              ]
            : []),
          ...(type === 'services'
            ? [
                {
                  type: FieldTypes.TextField,
                  label: 'Empresa',
                  hint: 'Ingrese el nombre de la empresa',
                  key: 'company',
                  errorMessage: 'La empresa es requerida',
                  withMargin: true,
                },
              ]
            : []),
          {
            type: FieldTypes.UploadImage,
            label: 'Subir Imagen',
            onChange: (event) => handleSelectFile(event),
            selectedFile: selfie ?? invitation?.selfie,
            inputRef: inputRef,
            withMargin: true,
          },
          {
            type: FieldTypes.DoubleCheckbox,
            withMargin: true,
            checkboxes: [
              {
                checked: invitationType === 1,
                onChange: () => setInvitationType(1),
                label: 'Acceso a unidad',
              },
              {
                checked: invitationType === 2,
                onChange: () => {
                  setInvitationType(2)
                  setValues({ ...values, apartments: [] })
                },
                label: 'Acceso a areas comunes',
              },
            ],
          },
          ...(invitationType === 1 && !building?.id
            ? [
                {
                  type: FieldTypes.Autocomplete,
                  label: 'Cliente',
                  hint: 'Seleccione un cliente',
                  key: 'buildingId',
                  options: buildingsState.buildings,
                  multiple: false,
                  freeSolo: false,
                  withMargin: true,
                  onChange: (value) => {
                    setSelectedBuilding(value)
                    getApartmentsFromBuilding(value.id)
                  },
                  errorMessage: 'El cliente es requerido',
                },
              ]
            : []),
          ...(invitationType === 1
            ? [
                {
                  type: FieldTypes.Autocomplete,
                  label: 'Unidad',
                  hint: 'Seleccione al menos una unidad',
                  key: 'apartments',
                  errorMessage: 'El apartamento es requerido',
                  options:
                    [
                      {
                        name: (building ?? selectedBuilding)?.name,
                        isGeneralService: true,
                      },
                      ...getApartmentsFromBuildingState?.apartments,
                    ] || [],
                  multiple: true,
                  freeSolo: false,
                  withMargin: true,
                  disabled:
                    getApartmentsFromBuildingState?.apartments.length === 0,
                  onChange: (value) => {
                    setValues({ ...values, apartments: value })
                  },
                },
              ]
            : []),
          ...(invitationType !== 0
            ? [
                {
                  type: FieldTypes.Schedules,
                  schedules: schedules,
                  updateScheduleData: (i, data) => updateScheduleData(i, data),
                  removeSchedule: (i) => removeSchedule(i),
                  setSchedules: () => setSchedules([...schedules, {}]),
                },
              ]
            : []),
          ...(invitationType === 0
            ? [
                {
                  type: FieldTypes.Autocomplete,
                  label: 'Evento',
                  hint: 'Seleccione un evento',
                  key: 'selectedEvent',
                  options: eventsState?.events || [],
                  multiple: false,
                  freeSolo: false,
                  withMargin: true,
                  disabled: !!building,
                  onChange: (value) => {
                    setValues({ ...values, selectedEvent: value })
                  },
                },
              ]
            : []),
        ]}
        handleSubmit={() => {
          let body
          if (invitationType === 0) {
            // add invitation to user event
            methods.addInvitation(
              values.selectedEvent.id,
              {
                firstName: values?.firstName,
                lastName: values?.lastName,
                phone: values?.phone,
                email: values?.email,
                ci: values.ci,
                selfie,
              },
              true
            )
          } else {
            body = {
              firstName: values?.firstName,
              lastName: values?.lastName,
              phone: values?.phone,
              email: values?.email,
              ci: values.ci,
              selfie,
              apartments:
                invitationType === 1
                  ? values.apartments.map((ap) => ap.id)
                  : [],
              schedules: schedules,
              buildingId: building.id,
              observations: values?.observations,
              enabled: true,
              isGeneralService:
                invitationType === 1
                  ? values.apartments[0].isGeneralService
                  : false,
            }
            if (type === 'services') {
              body.company = values?.company
              body.serviceType = userInvitationState?.serviceTypes?.find(
                (type) => type.id === values?.type
              ).name
            }
          }
          if (!invitation) {
            createUserInvitation(body)
          } else {
            updateUserInvitation(invitation.id, body)
          }
        }}
        handleCancel={handleCancel}
      />
    </div>
  )
}
